/**
 * The grid toolbar's three menus: which columns show and in what order,
 * how many stay frozen on the left, and the download.
 *
 * Each is a button that opens a panel (see Popover). The Columns panel is
 * also the keyboard way to do what dragging does in the header - the arrows
 * move a column one place - since a drag is no use to somebody who cannot
 * use a mouse.
 */
import { useRef, useState } from 'react';
import Icon from '../../lib/icons.jsx';
import Popover from './Popover.jsx';
import { MAX_FROZEN } from './layout.js';

function MenuButton({ icon, text, title, className = 'btn sm', count, panelLabel, panelClass, align = 'end', role, disabled, children }) {
  const [open, setOpen] = useState(false);
  const btn = useRef(null);
  const close = useRef(() => setOpen(false)).current;

  return (
    <>
      <button
        ref={btn}
        type="button"
        className={className}
        title={title}
        aria-haspopup={role === 'menu' ? 'menu' : 'dialog'}
        aria-expanded={open}
        disabled={disabled}
        onClick={() => setOpen(o => !o)}
      >
        <Icon name={icon} />{text}
        {count ? <span className="fcount">{count}</span> : null}
      </button>
      <Popover anchor={btn} open={open} onClose={close} label={panelLabel} className={panelClass} align={align} role={role}>
        {typeof children === 'function' ? children(close) : children}
      </Popover>
    </>
  );
}

/* ------------------------------------------------------------ columns */

export function ColumnsMenu({ columns, order, hidden, frozenIds, onToggle, onMove, onShowAll, onReset }) {
  const byId = new Map(columns.map(c => [c.id, c]));
  const list = order.filter(id => byId.has(id));
  const shown = list.filter(id => !hidden.has(id));
  const hiddenCount = list.length - shown.length;

  return (
    <MenuButton
      icon="eye"
      text="Columns"
      title="Show, hide and order the columns"
      count={hiddenCount ? `${shown.length}/${list.length}` : null}
      panelLabel="Columns"
      panelClass="dg-menu dg-cols"
    >
      <div className="dg-fphead">
        <Icon name="eye" />Columns
        <small>{shown.length} of {list.length} shown</small>
      </div>
      <ul className="dg-collist">
        {list.map(id => {
          const on = !hidden.has(id);
          const name = byId.get(id).meta?.label || id;
          const at = shown.indexOf(id);
          return (
            <li key={id} className={on ? '' : 'off'}>
              <label>
                <input
                  type="checkbox"
                  className="chk"
                  checked={on}
                  /* The last column showing stays: a table of nothing has no
                     header left to bring the others back from. */
                  disabled={on && shown.length === 1}
                  onChange={() => onToggle(id)}
                />
                <span>{name}</span>
                {frozenIds.includes(id) ? <em title="Frozen on the left"><Icon name="lock" /></em> : null}
              </label>
              <span className="dg-nudge">
                <button
                  type="button"
                  className="dg-up"
                  title={`Move ${name} left`}
                  aria-label={`Move ${name} left`}
                  disabled={!on || at <= 0}
                  onClick={() => onMove(id, -1)}
                >
                  <Icon name="chev" />
                </button>
                <button
                  type="button"
                  title={`Move ${name} right`}
                  aria-label={`Move ${name} right`}
                  disabled={!on || at < 0 || at >= shown.length - 1}
                  onClick={() => onMove(id, 1)}
                >
                  <Icon name="chev" />
                </button>
              </span>
            </li>
          );
        })}
      </ul>
      <div className="dg-fpacts dg-menufoot">
        <button type="button" className="frange" onClick={onShowAll} disabled={!hiddenCount}>
          <Icon name="eye" />Show all
        </button>
        {/* Order, widths, hidden columns, frozen columns, sort, page size and
            density - everything the table remembers, back to how it came. */}
        <button type="button" className="frange clear" onClick={onReset}>
          <Icon name="refresh" />Reset layout
        </button>
      </div>
    </MenuButton>
  );
}

/* ------------------------------------------------------------- freeze */

export function FreezeMenu({ frozen, fits, names, max, onPick }) {
  const most = Math.min(MAX_FROZEN, max);
  const word = n => (n === 1 ? 'column' : 'columns');

  return (
    <MenuButton
      icon="lock"
      text={frozen ? `Freeze ${frozen}` : 'Freeze'}
      title="Keep the first columns in view while the rest scroll sideways"
      className={`btn sm${frozen ? ' dg-lit' : ''}`}
      panelLabel="Freeze columns"
      panelClass="dg-menu"
      role="menu"
    >
      {close => (
        <>
          <div className="dg-fphead"><Icon name="lock" />Freeze columns</div>
          {Array.from({ length: most + 1 }, (_, n) => (
            <button
              key={n}
              type="button"
              role="menuitemradio"
              aria-checked={frozen === n}
              className={`dg-mitem${frozen === n ? ' on' : ''}`}
              onClick={() => { onPick(n); close(); }}
            >
              <span className="dg-tick">{frozen === n ? <Icon name="check" /> : null}</span>
              <span>
                {n ? `First ${n === 1 ? '' : `${n} `}${word(n)}` : 'None'}
                {n ? <small>{names.slice(0, n).join(', ')}</small> : <small>Every column scrolls</small>}
              </span>
            </button>
          ))}
          {/* A frozen block wider than the table leaves nothing to scroll
              into view, so on a narrow screen fewer are held than chosen. */}
          {fits < frozen ? (
            <p className="dg-fpnote">Only {fits} fit{fits === 1 ? 's' : ''} at this width; the rest scroll.</p>
          ) : null}
        </>
      )}
    </MenuButton>
  );
}

/* ----------------------------------------------------------- download */

export function DownloadMenu({ busy, count, offline, onPick }) {
  return (
    <MenuButton
      icon={busy ? 'refresh' : 'down'}
      text={busy ? 'Preparing…' : 'Download'}
      title="Download every row that matches, in the columns and order shown"
      className="btn sm expbtn"
      panelLabel="Download"
      panelClass="dg-menu"
      role="menu"
      disabled={busy}
    >
      {close => (
        <>
          <div className="dg-fphead">
            <Icon name="down" />Download
            <small>{count} row{count === 1 ? '' : 's'}</small>
          </div>
          <button type="button" role="menuitem" className="dg-mitem" onClick={() => { close(); onPick('csv'); }}>
            <span className="dg-tick"><Icon name="file" /></span>
            <span>CSV<small>Opens anywhere; plain values</small></span>
          </button>
          <button
            type="button"
            role="menuitem"
            className="dg-mitem"
            disabled={offline}
            onClick={() => { close(); onPick('xlsx'); }}
          >
            <span className="dg-tick"><Icon name="sheet" /></span>
            <span>
              Excel
              <small>{offline ? 'Needs the server — not available offline' : 'Laid out, with real dates and numbers'}</small>
            </span>
          </button>
        </>
      )}
    </MenuButton>
  );
}
